import {
  type BrandSettings,
  DEFAULT_ABNORMAL,
  effectivePrint,
  emailProblem,
  LABVIEW_CONTACT_LINE,
  letterheadPreset,
  PRINT_GROUPS,
  type PrintItem,
  printsEverything,
} from '@quickreport/domain';
import { ImagePlus, Plus, Trash2, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { AbnormalPicker } from './abnormal';
import { Button, Field, Input, Select, Textarea } from './ui';

/**
 * The forms for a lab's report settings, shared by the lab's own app and the administrator
 * console, so both look and behave the same. They hold no data of their own: the page that uses
 * them says where settings, pictures and signatories are kept.
 */

/** A picture's bytes and type, as kept by whichever page shows it. */
export interface ImageData {
  bytes: Uint8Array;
  mime: string;
}

export type SetBrand = (p: Partial<BrandSettings>) => void;

const SWATCHES = ['#2b2157', '#4b3a8f', '#d9668a', '#0e7c86', '#2e7d4f', '#222222'];
const REG_SUGGESTIONS = [
  'NABL',
  'State registration (CE)',
  'HFR ID',
  'Clinical Establishment No.',
  'GSTIN',
];

function useImageUrl(image: ImageData | null): string | null {
  return useMemo(
    () =>
      image ? URL.createObjectURL(new Blob([image.bytes as BlobPart], { type: image.mime })) : null,
    [image],
  );
}

export function BrandDetails({ brand, set }: { brand: BrandSettings; set: SetBrand }) {
  const regs = brand.registrations;
  const setReg = (i: number, patch: Partial<{ label: string; value: string }>) =>
    set({ registrations: regs.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <>
      <Field label="Lab name">
        <Input
          value={brand.labName}
          onChange={(e) => set({ labName: e.target.value })}
          placeholder="ABC Diagnostics"
        />
      </Field>
      <Field label="Tagline" hint="Optional, printed under the name">
        <Input
          value={brand.tagline ?? ''}
          onChange={(e) => set({ tagline: e.target.value || undefined })}
          placeholder="Accurate · Caring · Instant"
        />
      </Field>
      <Field label="Address">
        <Textarea
          rows={2}
          value={brand.address}
          onChange={(e) => set({ address: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Phone numbers">
          <Input
            value={brand.phones}
            onChange={(e) => set({ phones: e.target.value })}
            placeholder="+91 90000 00000, 0326-247821"
          />
        </Field>
        <Field label="Email" error={emailProblem(brand.email ?? '')}>
          <Input
            value={brand.email ?? ''}
            onChange={(e) => set({ email: e.target.value || undefined })}
          />
        </Field>
      </div>
      <Field
        label="Lab number prefix"
        hint="Optional. Letters that start every new lab number. For example SUN makes SUN-2026-000001; leave it empty for 2026-000001. Reports you have already made keep their numbers."
      >
        <Input
          value={brand.labNumberPrefix ?? ''}
          onChange={(e) =>
            set({
              labNumberPrefix:
                e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, '')
                  .slice(0, 6) || undefined,
            })
          }
          placeholder="SUN"
          className="w-32 font-mono"
        />
      </Field>
      <section className="grid gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-[13px] font-semibold">Registration numbers</h2>
          <Button
            size="sm"
            onClick={() => set({ registrations: [...regs, { label: '', value: '' }] })}
          >
            <Plus size={13} /> Add
          </Button>
        </div>
        <datalist id="reg-labels">
          {REG_SUGGESTIONS.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
        {regs.length === 0 ? (
          <p className="text-[12px] text-slate">
            Shown at the top right of every report, e.g. NABL certificate or HFR ID.
          </p>
        ) : null}
        {regs.map((r, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows have no ids
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
            <Input
              list="reg-labels"
              aria-label="Label"
              placeholder="Label"
              value={r.label}
              onChange={(e) => setReg(i, { label: e.target.value })}
            />
            <Input
              aria-label="Number"
              placeholder="Number"
              value={r.value}
              onChange={(e) => setReg(i, { value: e.target.value })}
            />
            <button
              type="button"
              aria-label="Remove"
              className="px-1 text-slate hover:text-flag"
              onClick={() => set({ registrations: regs.filter((_, j) => j !== i) })}
            >
              <X size={15} />
            </button>
          </div>
        ))}
      </section>
      <Field
        label="Line printed at the foot of every page"
        hint="e.g. Results should be correlated clinically."
      >
        <Input
          value={brand.disclaimer ?? ''}
          onChange={(e) => set({ disclaimer: e.target.value || undefined })}
        />
      </Field>
    </>
  );
}

export function BrandLook({
  brand,
  set,
  logo,
  onLogo,
}: {
  brand: BrandSettings;
  set: SetBrand;
  logo: ImageData | null;
  onLogo: (f: File | undefined) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const logoUrl = useImageUrl(logo);
  return (
    <>
      <section className="grid gap-2">
        <h2 className="text-[13px] font-semibold">Logo</h2>
        <div className="flex items-center gap-4">
          <div className="grid h-20 w-32 place-items-center overflow-hidden rounded-md border border-line bg-paper">
            {logoUrl ? (
              <img src={logoUrl} alt="Your logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-[12px] text-slate">No logo</span>
            )}
          </div>
          <div className="grid gap-2">
            <input
              ref={input}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={(e) => onLogo(e.target.files?.[0])}
            />
            <Button onClick={() => input.current?.click()}>
              <ImagePlus size={15} /> {logo ? 'Change logo' : 'Upload logo'}
            </Button>
            {logo ? (
              <Button tone="ghost" onClick={() => set({ logoFileId: undefined })}>
                Remove
              </Button>
            ) : null}
          </div>
        </div>
        <p className="text-[12px] text-slate">
          PNG, JPEG or SVG. A wide logo on a white or transparent background works best.
        </p>
      </section>
      <section className="grid gap-2">
        <h2 className="text-[13px] font-semibold">Lab name on the report</h2>
        <Select
          aria-label="Where the lab name appears"
          value={brand.nameAt}
          onChange={(e) => set({ nameAt: e.target.value as BrandSettings['nameAt'] })}
          className="max-w-sm"
        >
          <option value="right">After the logo (to the right of it)</option>
          <option value="left">Before the logo (to the left of it)</option>
          <option value="above">Above the logo</option>
          <option value="below">Below the logo</option>
          <option value="hidden">Do not show the lab name</option>
        </Select>
        <p className="text-[12px] text-slate">
          Choose "Do not show" if your logo already contains the name. The address and phone numbers
          still print.
        </p>
      </section>
      <section className="grid gap-2">
        <h2 className="text-[13px] font-semibold">Colour</h2>
        <div className="flex flex-wrap items-center gap-2">
          {SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Use colour ${c}`}
              aria-pressed={brand.accent.toLowerCase() === c}
              onClick={() => set({ accent: c })}
              className={`h-8 w-8 rounded-full border-2 ${brand.accent.toLowerCase() === c ? 'border-ink' : 'border-transparent'}`}
              style={{ background: c }}
            />
          ))}
          <Input
            aria-label="Colour code"
            value={brand.accent}
            onChange={(e) => set({ accent: e.target.value })}
            className="ml-2 w-28 font-mono"
            spellCheck={false}
          />
        </div>
        <p className="text-[12px] text-slate">
          Used for the lab name and the line under the header. Everything else stays black on white,
          so it prints well on any printer.
        </p>
      </section>
      <section className="grid gap-2">
        <div>
          <h2 className="text-[13px] font-semibold">Values outside the normal range</h2>
          <p className="text-[12px] text-slate">
            How an abnormal result prints on every report. Some patients do not like red on their
            report, so choose what suits your lab. You can still change it for a single report or
            result when you are writing it.
          </p>
        </div>
        <AbnormalPicker
          value={{
            bold: brand.abnormalBold ?? DEFAULT_ABNORMAL.bold,
            color: brand.abnormalColor ?? DEFAULT_ABNORMAL.color,
          }}
          onChange={(l) => set({ abnormalBold: l.bold, abnormalColor: l.color })}
        />
      </section>
    </>
  );
}

export function BrandPrivacy({ brand, set }: { brand: BrandSettings; set: SetBrand }) {
  const on = brand.requirePatientCode === true;
  return (
    <>
      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line bg-paper p-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-brand"
          checked={on}
          onChange={(e) => set({ requirePatientCode: e.target.checked })}
        />
        <span>
          <span className="block font-medium">
            Ask the patient for a code before their report opens
          </span>
          <span className="block text-[13px] text-slate">
            When this is ticked, a patient who opens the QR code or the verification link first gets
            a 6-digit code on their WhatsApp or email and must enter it. When it is not ticked (the
            default), the link opens the report straight away.
          </span>
        </span>
      </label>
      <p className="rounded-lg border border-line bg-canvas p-3 text-[13px] text-slate">
        {on
          ? 'Anyone who opens a report must first enter the code sent to the patient.'
          : 'Anyone who has a report’s link or QR code can open it, so share the link only with the patient.'}{' '}
        Reports you mark as sensitive always ask for the code, whatever you choose here.
      </p>
    </>
  );
}

export function BrandPage({
  brand,
  set,
  background,
  onBackground,
  onFitBackground,
}: {
  brand: BrandSettings;
  set: SetBrand;
  /** The letterhead picture, when one is set. */
  background?: ImageData | null;
  /** Pick (or, with nothing, remove) the letterhead picture. Omitted: the option is not shown. */
  onBackground?: (file: File | undefined) => void;
  /** Measure the picture again and set the margins and printed items for it. */
  onFitBackground?: () => void;
}) {
  const all = printsEverything(brand);
  const choices = effectivePrint(brand);
  const signaturesPrinted = choices.signatureImage || choices.signatoryNames;
  const setItem = (id: PrintItem, on: boolean) =>
    set({ printAll: false, letterhead: true, print: { ...choices, [id]: on } });
  return (
    <>
      {onBackground ? (
        <BackgroundPicker
          brand={brand}
          image={background ?? null}
          onPick={onBackground}
          onFit={onFitBackground}
        />
      ) : null}
      <label className="flex gap-3 rounded-lg border border-line p-3">
        <input
          type="checkbox"
          checked={all}
          onChange={(e) =>
            e.target.checked
              ? set({ printAll: true, letterhead: false })
              : set({
                  printAll: false,
                  letterhead: true,
                  print: { ...letterheadPreset(), ...(brand.print ?? {}) },
                })
          }
          className="mt-1 h-4 w-4 accent-brand"
        />
        <span>
          <span className="block font-medium">Print everything</span>
          <span className="block text-[13px] text-slate">
            Logo, lab name, address, signatures and the rest. Untick this if you print on
            pre-printed letterhead, then choose below what your paper does not already have.
          </span>
        </span>
      </label>

      <fieldset
        disabled={all}
        className={`grid gap-4 rounded-lg border border-line p-3 ${all ? 'bg-canvas opacity-60' : ''}`}
      >
        <legend className="px-1 text-[13px] font-semibold">
          {all ? 'Everything below is printed' : 'Print only what is ticked'}
        </legend>
        {PRINT_GROUPS.map((g) => (
          <div key={g.title}>
            <h3 className="mb-1.5 text-[12px] font-semibold tracking-wide text-slate uppercase">
              {g.title}
            </h3>
            <div className="grid gap-1 sm:grid-cols-2">
              {g.items.map((item) => (
                <label
                  key={item.id}
                  className={`flex min-h-10 items-center gap-2.5 rounded-md px-2 py-1.5 ${all ? '' : 'hover:bg-brand-soft'}`}
                >
                  <input
                    type="checkbox"
                    checked={choices[item.id]}
                    onChange={(e) => setItem(item.id, e.target.checked)}
                    className="h-4 w-4 accent-brand"
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
        <p className="text-[12px] text-slate">
          The patient's details, the results and the “End of report” line always print.
        </p>
      </fieldset>

      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line bg-paper p-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-brand"
          checked={brand.labviewFooter ?? true}
          onChange={(e) => set({ labviewFooter: e.target.checked })}
        />
        <span>
          <span className="block font-medium">
            Print the LabView line at the bottom of each page
          </span>
          <span className="block text-[13px] text-slate">
            A small grey line in the footer: {LABVIEW_CONTACT_LINE}. It is on unless you untick it.
            It is not part of “Print everything”, so it stays if you print on your own letterhead.
          </span>
        </span>
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Space at the top (mm)"
          hint={all ? 'Room for the header' : 'Below your letterhead header'}
        >
          <Input
            type="number"
            min={10}
            max={120}
            value={brand.topMarginMm}
            onChange={(e) => set({ topMarginMm: Number(e.target.value) })}
          />
        </Field>
        <Field label="Space at the bottom (mm)" hint="Room for signatures and the footer">
          <Input
            type="number"
            min={10}
            max={80}
            value={brand.bottomMarginMm}
            onChange={(e) => set({ bottomMarginMm: Number(e.target.value) })}
          />
        </Field>
      </div>
      <Field label="Signatures">
        <Select
          value={brand.signaturesOn}
          disabled={!signaturesPrinted}
          onChange={(e) => set({ signaturesOn: e.target.value as BrandSettings['signaturesOn'] })}
        >
          <option value="every-page">On every page (as most Indian labs print)</option>
          <option value="last-page">On the last page only</option>
        </Select>
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Signature height (mm)"
          hint="Room above the line for the signature. Taller leaves more space for a hand signature."
        >
          <Input
            type="number"
            min={6}
            max={30}
            value={brand.signatureHeightMm}
            disabled={!signaturesPrinted}
            onChange={(e) => set({ signatureHeightMm: Number(e.target.value) })}
          />
        </Field>
        <Field
          label="Signature width (mm)"
          hint="Width of each signature block. Three fit across the page; more go on a second row."
        >
          <Input
            type="number"
            min={40}
            max={60}
            value={brand.signatureWidthMm}
            disabled={!signaturesPrinted}
            onChange={(e) => set({ signatureWidthMm: Number(e.target.value) })}
          />
        </Field>
      </div>
      <p className="text-[12px] text-slate">
        Signatures start at the left: one is on the left, two are left and centre, three fill the
        row. The space at the bottom grows by itself when the signatures are taller.
      </p>
      <p className="text-[12px] text-slate">
        Paper size is A4. Use “Sample PDF” at the top to print a test page and check the fit.
      </p>
    </>
  );
}

export interface SignatoryItem {
  id: string;
  name: string;
  qualification: string;
  designation: string | null;
  regNo: string | null;
}

export interface SignatoryValues {
  name: string;
  qualification: string;
  designation: string | null;
  regNo: string | null;
  /** Left out when the picture was not changed. */
  signatureFileId?: string | null;
}

/** The doctors who sign reports: their details and signature pictures. */
export function SignatoryManager<T extends SignatoryItem>({
  items,
  images,
  canEdit,
  onSave,
  onRemove,
  onPickImage,
}: {
  items: T[];
  images: Record<string, ImageData | null>;
  canEdit: boolean;
  /** Keep a new or changed signatory. `existing` is null for a new one. */
  onSave: (values: SignatoryValues, existing: T | null) => Promise<void>;
  onRemove: (item: T) => Promise<void> | void;
  /** Prepare and keep a picked picture; says what it is called and how it looks. */
  onPickImage: (
    file: File,
    opts: { whiteToTransparent: boolean },
  ) => Promise<{ fileId: string; image: ImageData }>;
}) {
  const [editing, setEditing] = useState<{ item: T | null } | null>(null);
  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-slate">
          The doctors who sign reports. Their name, qualification, registration number and signature
          print at the foot of each report.
        </p>
        {canEdit ? (
          <Button onClick={() => setEditing({ item: null })}>
            <Plus size={15} /> Add
          </Button>
        ) : null}
      </div>
      {items.length === 0 && !editing ? (
        <p className="rounded-lg border border-dashed border-line p-6 text-center text-slate">
          No signatories yet. Add the pathologist who will sign the reports.
        </p>
      ) : null}
      <ul className="grid gap-2">
        {items.map((s) => (
          <li
            key={s.id}
            className="flex items-center gap-4 rounded-lg border border-line bg-paper p-3"
          >
            <div className="grid h-12 w-24 place-items-center overflow-hidden rounded bg-canvas">
              {images[s.id] ? (
                <SigImg image={images[s.id] as ImageData} />
              ) : (
                <span className="text-[11px] text-slate">No signature</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium">{s.name}</div>
              <div className="text-[13px] text-slate">
                {[s.qualification, s.designation, s.regNo ? `Reg. ${s.regNo}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            {canEdit ? (
              <>
                <Button size="sm" onClick={() => setEditing({ item: s })}>
                  Edit
                </Button>
                <button
                  type="button"
                  aria-label={`Remove ${s.name}`}
                  className="p-1 text-slate hover:text-flag"
                  onClick={() => void onRemove(s)}
                >
                  <Trash2 size={15} />
                </button>
              </>
            ) : null}
          </li>
        ))}
      </ul>
      {editing ? (
        <SignatoryForm
          item={editing.item}
          image={editing.item ? (images[editing.item.id] ?? null) : null}
          onPickImage={onPickImage}
          onSave={async (values) => {
            await onSave(values, editing.item);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function SigImg({ image }: { image: ImageData }) {
  const url = useImageUrl(image);
  return url ? (
    <img src={url} alt="Signature" className="max-h-full max-w-full object-contain" />
  ) : null;
}

function SignatoryForm<T extends SignatoryItem>({
  item,
  image,
  onPickImage,
  onSave,
  onClose,
}: {
  item: T | null;
  image: ImageData | null;
  onPickImage: (
    file: File,
    opts: { whiteToTransparent: boolean },
  ) => Promise<{ fileId: string; image: ImageData }>;
  onSave: (values: SignatoryValues) => Promise<void>;
  onClose: () => void;
}) {
  const [f, setF] = useState({
    name: item?.name ?? '',
    qualification: item?.qualification ?? '',
    designation: item?.designation ?? '',
    regNo: item?.regNo ?? '',
    signatureFileId: null as string | null,
    keepPicture: true,
  });
  const [preview, setPreview] = useState<ImageData | null>(image);
  const [transparent, setTransparent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    try {
      const r = await onPickImage(file, { whiteToTransparent: transparent });
      setF((x) => ({ ...x, signatureFileId: r.fileId, keepPicture: false }));
      setPreview(r.image);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not use that picture.');
    }
  }

  async function submit() {
    if (!f.name.trim()) {
      setError('Enter the name.');
      return;
    }
    setBusy(true);
    try {
      await onSave({
        name: f.name,
        qualification: f.qualification,
        designation: f.designation || null,
        regNo: f.regNo || null,
        // Left out when not changed: the host keeps the picture the signatory already has.
        ...(f.keepPicture ? {} : { signatureFileId: f.signatureFileId }),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <fieldset
      className="grid min-w-0 gap-4 rounded-lg border border-brand bg-brand-soft/30 p-4"
      aria-label="Signatory details"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name">
          <Input
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="Dr. PQR"
          />
        </Field>
        <Field label="Qualification (optional)">
          <Input
            value={f.qualification}
            onChange={(e) => setF({ ...f, qualification: e.target.value })}
            placeholder="MBBS, MD (Pathology)"
          />
        </Field>
        <Field label="Designation">
          <Input
            value={f.designation}
            onChange={(e) => setF({ ...f, designation: e.target.value })}
            placeholder="Consultant Pathologist"
          />
        </Field>
        <Field label="Registration number">
          <Input
            value={f.regNo}
            onChange={(e) => setF({ ...f, regNo: e.target.value })}
            placeholder="34518"
          />
        </Field>
      </div>
      <div className="flex items-center gap-4">
        <div className="grid h-16 w-32 place-items-center overflow-hidden rounded border border-line bg-paper">
          {preview ? (
            <SigImg image={preview} />
          ) : (
            <span className="text-[11px] text-slate">No signature</span>
          )}
        </div>
        <div className="grid gap-1.5">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            className="hidden"
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <Button size="sm" onClick={() => input.current?.click()}>
            <ImagePlus size={14} /> {preview ? 'Change signature' : 'Upload signature'}
          </Button>
          <label className="flex items-center gap-1.5 text-[12px] text-slate">
            <input
              type="checkbox"
              checked={transparent}
              onChange={(e) => setTransparent(e.target.checked)}
              className="accent-brand"
            />
            Make the white paper background transparent
          </label>
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-[13px] text-flag">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button tone="primary" disabled={busy} onClick={() => void submit()}>
          Save signatory
        </Button>
      </div>
    </fieldset>
  );
}

/** The lab's own letterhead as a picture behind every page. */
function BackgroundPicker({
  brand,
  image,
  onPick,
  onFit,
}: {
  brand: BrandSettings;
  image: ImageData | null;
  onPick: (file: File | undefined) => void;
  onFit?: (() => void) | undefined;
}) {
  const input = useRef<HTMLInputElement>(null);
  const url = useImageUrl(image);
  const clash = Boolean(image) && printsEverything(brand);
  return (
    <section className="grid gap-3 rounded-lg border border-line bg-paper p-3">
      <div>
        <h2 className="text-[13px] font-semibold">Letterhead picture</h2>
        <p className="text-[12px] text-slate">
          Use your own letterhead as the background of every page: a picture of the whole A4 page
          with your logo, address and contact lines. The report is printed in the space it leaves.
        </p>
      </div>
      <div className="flex items-start gap-4">
        <div className="grid h-28 w-20 shrink-0 place-items-center overflow-hidden rounded border border-line bg-canvas">
          {url ? (
            <img src={url} alt="Your letterhead" className="h-full w-full object-contain" />
          ) : (
            <span className="px-1 text-center text-[11px] text-slate">No picture</span>
          )}
        </div>
        <div className="grid gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => input.current?.click()}>
              <ImagePlus size={14} /> {image ? 'Change picture' : 'Upload your letterhead'}
            </Button>
            {image && onFit ? (
              <Button size="sm" onClick={onFit}>
                Fit the page to the picture
              </Button>
            ) : null}
            {image ? (
              <Button size="sm" tone="ghost" onClick={() => onPick(undefined)}>
                Remove
              </Button>
            ) : null}
          </div>
          <p className="text-[12px] text-slate">
            A JPEG or PNG of the whole page, portrait A4. When you upload it, your own header is
            switched off and the space at the top and bottom is set from the picture. You can still
            change them below.
          </p>
        </div>
      </div>
      {clash ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-[12px] text-warn">
          “Print everything” is on, so your own header will print over the picture. Press “Fit the
          page to the picture”, or untick it and choose what to print below.
        </p>
      ) : null}
    </section>
  );
}
